import { Button, Checkbox, Row, Separator, useOverlay } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import UserReservationsInfiniteScroll from './UserReservationsInfiniteScroll'
import { getUserReservationData } from '../../../services/userService'
// import requestStatus from '../../../util/requestStatus'
import { PermissionsContext } from '../../app/PermissionsContext'
import { UserContext } from '../UserContext'
import ReleaseReservationDialog from '../../reservation/ReleaseReservationDialog'
import UserReservationsDownloadDialog from './UserReservationsDownloadDialog'

export default function UserReservations() {
  usePageView({ page_detail: 'DMConsole:UserReservations' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.reservations'))
  const { currentUser, fetchCurrentUser } = useContext(UserContext)
  const { isAuthorized } = useContext(PermissionsContext)
  // const [fetchStatus, setFetchStatus] = useState(requestStatus.NOT_STARTED)
  const [checkedRanIds, setCheckedRanIds] = useState([])
  const [userReservations, setUserReservations] = useState({})
  const [showOnlyCompletedOrdinances, setShowOnlyCompletedOrdinances] = useState(false)
  const [totalPersonsNotShared, setTotalPersonsNotShared] = useState(0)
  const [totalPersonsShared, setTotalPersonsShared] = useState(0)
  const [disableDownload, setDisableDownload] = useState(true)
  const [disableDisplay, setDisableDisplay] = useState(false)
  const downloadReservationsDialogOpener = useOverlay()
  const MAX_RESERVATIONS_TO_DOWNLOAD = 100000
  const MAX_RESERVATIONS_TO_DISPLAY = 1000

  const releaseDialogOpener = useOverlay()

  const isChecked = (ranId) => checkedRanIds.includes(ranId)

  const anyChecked = () => checkedRanIds.length > 0

  useEffect(() => {
    // If there is no contributorId there are no reservations, so skip the
    // limit checks entirely rather than requesting `users/null/reservations`.
    // Keep download disabled too: there is nothing to download, and enabling
    // it would let a click hit `users/null/downloadReservations`.
    if (currentUser.contributorId == null) {
      setDisableDownload(true)
      setDisableDisplay(false)
      return
    }

    const checkDownloadLimit = () => {
      getUserReservationData(
        currentUser.contributorId,
        MAX_RESERVATIONS_TO_DOWNLOAD,
        1,
        showOnlyCompletedOrdinances
      ).then((response) => {
        if (response.data.reservations && response.data.reservations.length > 0) {
          setDisableDownload(true)
          return true
        }
        setDisableDownload(false)
        return false
      })
    }

    const checkDisplayLimit = () => {
      getUserReservationData(
        currentUser.contributorId,
        MAX_RESERVATIONS_TO_DISPLAY,
        1,
        showOnlyCompletedOrdinances
      ).then((response) => {
        if (response.data.reservations && response.data.reservations.length > 0) {
          setDisableDisplay(() => true)
          return true
        }
        return false
      })
    }
    checkDisplayLimit()
    checkDownloadLimit()
  }, [currentUser.contributorId, showOnlyCompletedOrdinances])

  const updateShowOnlyCompletedOrdinances = ({ target }) => {
    setShowOnlyCompletedOrdinances(target.checked)
    // setFetchStatus(requestStatus.STARTED)
  }

  return (
    <div>
      <Row alignY="middle">
        <Button
          variant="contained"
          emphasis="high"
          onClick={releaseDialogOpener.open}
          disabled={!isAuthorized('ReserveOrdinancePermission') || !anyChecked()}
        >
          {t('action.releaseSelected', 'Release Selected')}
        </Button>
        <Button
          variant="contained"
          emphasis="medium"
          onClick={downloadReservationsDialogOpener.handleClick}
          disabled={disableDownload}
        >
          {t('action.download', 'Download')}
        </Button>
        <UserReservationsDownloadDialog
          disableDownload={disableDownload}
          showOnlyCompletedOrdinances={showOnlyCompletedOrdinances}
          {...downloadReservationsDialogOpener}
        />
        {!showOnlyCompletedOrdinances && (
          <Row>
            <div>{`${t('reservations.totalPersonsNotShared', 'Persons not Shared:')} ${totalPersonsNotShared}`}</div>
            <div>{`${t('reservations.totalPersonsShared', 'Persons Shared:')} ${totalPersonsShared}`}</div>
          </Row>
        )}
        <Checkbox
          color="primary"
          name="showCompletedOrdinances"
          checked={showOnlyCompletedOrdinances}
          onChange={updateShowOnlyCompletedOrdinances}
          disabled={!isAuthorized('ReserveOrdinancePermission')}
          label={t('reservations.showNotInList', 'Show Not in List')}
        />
      </Row>
      <Separator size="sm" />
      {!disableDisplay && (
        <UserReservationsInfiniteScroll
          handleCheck={setCheckedRanIds}
          checkedRanIds={checkedRanIds}
          handleReservations={setUserReservations}
          handleTotalPersonsNotShared={setTotalPersonsNotShared}
          handleTotalPersonsShared={setTotalPersonsShared}
          showOnlyCompletedOrdinances={showOnlyCompletedOrdinances}
        />
      )}
      {anyChecked() && (
        <ReleaseReservationDialog
          {...releaseDialogOpener}
          onRelease={fetchCurrentUser}
          reservationData={userReservations}
          isChecked={isChecked}
          checkedRanIds={checkedRanIds}
        />
      )}
      {disableDisplay && (
        <div>{t('reservations.displayLimitExceeded', 'More than 1000 reservations. Unable to display list.')}</div>
      )}
      {disableDownload && currentUser.contributorId != null && (
        <div>{t('reservations.downloadLimitExceeded', 'More than 100K reservations. Unable to download list.')}</div>
      )}
    </div>
  )
}
